import { useState } from 'react';
import { api, type AllotmentReading } from '../lib/api';
import { longDate, money } from '../lib/format';
import { Badge, Card, EmptyState, SectionHeading } from './ui';
import { Icon } from './icons';

/**
 * Held allotment readings, and the two things a preparer can do with one.
 *
 * Reading a filed SH01 produces evidence or it produces a question. The
 * evidence goes straight to the transactions list; the questions came here
 * to nowhere — they lived on the audit trail, which is a record of what
 * happened and deliberately not a place anything can be done. So a held
 * reading was visible to whoever ran the SQL and to nobody else, and Stage 4
 * anchored on a set of transactions with a hole in it that nothing on the
 * screen could show.
 *
 * The panel is built around one refusal: **the price the model read is shown
 * but never submitted.** It sits next to an empty field the preparer types
 * into, because a confirmation that starts pre-filled is a click, and a click
 * is not a person having checked a figure against a filed form. Everything
 * else here follows from that — the form link opens beside the field, the
 * reasons the line was held stay on screen while it is being answered, and
 * the relationship question is asked plainly rather than defaulted, since
 * `arm's length` is the answer that makes Stage 4 demand an anchor.
 */
export function HeldReadings({
  engagementId,
  readings,
  canWrite,
  onChange,
}: {
  engagementId: string;
  readings: AllotmentReading[];
  canWrite: boolean;
  onChange: () => void | Promise<void>;
}) {
  const held = readings.filter((r) => r.status === 'held');
  const resolved = readings.filter((r) => r.status !== 'held');
  const [showResolved, setShowResolved] = useState(false);

  if (readings.length === 0) return null;

  return (
    <Card>
      <SectionHeading
        title="Filed allotments awaiting a reading"
        subtitle="The register said an allotment happened and the form was read; these lines were not written as evidence"
        right={
          held.length > 0 ? (
            <Badge tone="amber">
              {held.length} held
            </Badge>
          ) : (
            <Badge tone="emerald">All resolved</Badge>
          )
        }
      />

      {held.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          detail="Every allotment the register produced has been confirmed as evidence or dismissed with a reason."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {held.map((reading) => (
            <li key={reading.id} data-item-id={reading.id} className="scroll-mt-24">
              <HeldReadingRow
                engagementId={engagementId}
                reading={reading}
                canWrite={canWrite}
                onChange={onChange}
              />
            </li>
          ))}
        </ul>
      )}

      {resolved.length > 0 ? (
        <div className="border-t border-slate-100 px-6 py-3">
          <button
            type="button"
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved ? 'Hide' : 'Show'} {resolved.length} resolved reading
            {resolved.length === 1 ? '' : 's'}
          </button>
          {showResolved ? (
            <ul className="mt-3 space-y-3">
              {resolved.map((r) => (
                <li key={r.id} data-item-id={r.id} className="scroll-mt-24 text-sm">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <Badge tone={r.status === 'confirmed' ? 'emerald' : 'slate'}>{r.status}</Badge>
                    <span className="text-slate-700">
                      {r.filingType} · {r.shareClass}
                      {r.filingDate ? ` · filed ${longDate(r.filingDate)}` : ''}
                    </span>
                  </div>
                  {/* A dismissed reading is the answer to "why did you not
                      anchor to that transaction?", so its reason is shown
                      rather than folded away with the row. */}
                  {r.resolutionNote ? (
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{r.resolutionNote}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

type Mode = 'closed' | 'confirm' | 'dismiss';

/**
 * A share count as a person would write it. The column is `numeric(18,4)`
 * because a partly paid allotment can be in fractions, so the wire carries
 * `60000.0000` — which on the page reads as a machine talking.
 */
function shareCount(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-GB', { maximumFractionDigits: 4 }) : value;
}

function HeldReadingRow({
  engagementId,
  reading,
  canWrite,
  onChange,
}: {
  engagementId: string;
  reading: AllotmentReading;
  canWrite: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [mode, setMode] = useState<Mode>('closed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deliberately empty. The figure the model read is displayed a few lines
  // up; typing it again is the check.
  const [pricePerShare, setPricePerShare] = useState('');
  const [txnDate, setTxnDate] = useState(reading.allotmentDate ?? reading.filingDate ?? '');
  const [shareClass, setShareClass] = useState(reading.shareClass);
  const [shares, setShares] = useState(
    reading.shares ? String(Number(reading.shares)) : '',
  );
  const [considerationKind, setConsiderationKind] = useState(
    reading.considerationKind === 'unknown' ? 'cash' : reading.considerationKind,
  );
  const [relationship, setRelationship] = useState('unknown');
  const [provenance, setProvenance] = useState('registry_confirmed');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const priceMatchesReading =
    reading.pricePerShare !== null &&
    pricePerShare.trim() !== '' &&
    Number(pricePerShare) === Number(reading.pricePerShare);

  const confirmValid =
    /^\d{4}-\d{2}-\d{2}$/.test(txnDate) &&
    /^\d+(\.\d+)?$/.test(pricePerShare.trim()) &&
    note.trim().length >= 10;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setMode('closed');
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resolve the reading');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg px-6 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">
          {reading.filingType}
          {reading.filingDate ? ` filed ${longDate(reading.filingDate)}` : ''}
        </span>
        <span className="tnum text-sm text-slate-500">
          {reading.pricePerShare
            ? `read as ${money(reading.pricePerShare)} / share`
            : 'no price per share on the form'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge tone="slate">{reading.shareClass}</Badge>
        {reading.shares ? <Badge tone="slate">{shareCount(reading.shares)} shares</Badge> : null}
        <Badge tone={reading.considerationKind === 'cash' ? 'slate' : 'amber'}>
          {reading.considerationKind.replace(/_/g, ' ')} consideration
        </Badge>
        {reading.confidence !== null ? (
          <Badge
            tone={reading.confidence >= 0.9 ? 'slate' : 'amber'}
            title="How sure the reading was of this line"
          >
            confidence {reading.confidence.toFixed(2)}
          </Badge>
        ) : null}
      </div>

      {/* The reasons are what the preparer is being asked to overrule, so
          they stay on screen while the form below is being filled in. */}
      <ul className="mt-2.5 space-y-1">
        {reading.holdReasons.map((r, i) => (
          <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-600">
            <Icon name="alert" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>{r}</span>
          </li>
        ))}
      </ul>

      {reading.considerationDescription ? (
        <p className="mt-2 text-sm italic leading-relaxed text-slate-500">
          The form describes the consideration as: {reading.considerationDescription}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {reading.documentUrl ? (
          <a
            href={reading.documentUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-sky-700 underline underline-offset-2 hover:text-sky-900"
          >
            Open the filed form
          </a>
        ) : null}
        {reading.promptTemplate ? (
          <span className="text-xs text-slate-400">read by {reading.promptTemplate}</span>
        ) : null}
      </div>

      {canWrite && mode === 'closed' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => setMode('confirm')}>
            Confirm against the form
          </button>
          <button type="button" className="btn-secondary" onClick={() => setMode('dismiss')}>
            Not evidence of value
          </button>
        </div>
      ) : null}

      {mode === 'confirm' ? (
        <div className="mt-4 space-y-4 rounded-lg bg-slate-50/80 p-4">
          <p className="text-sm leading-relaxed text-slate-600">
            Read the price off the filed form and type it here. It is not filled in from the
            reading on purpose: a transaction in the subject shares can decide the valuation, and
            what makes this one evidence is that a person read the form.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">Price per share, as printed</span>
              <input
                autoFocus
                inputMode="decimal"
                value={pricePerShare}
                onChange={(e) => setPricePerShare(e.target.value)}
                placeholder="e.g. 2.50"
                className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
              {/* Agreement is worth saying, because it is the ordinary case
                  and its absence is the interesting one: a figure that
                  differs from the reading is recorded as amended, and the
                  audit trail says so. */}
              {pricePerShare.trim() && reading.pricePerShare ? (
                <span
                  className={`mt-1 block text-xs ${
                    priceMatchesReading ? 'text-slate-400' : 'text-amber-700'
                  }`}
                >
                  {priceMatchesReading
                    ? 'Agrees with the reading.'
                    : `The reading said ${money(reading.pricePerShare)}. Yours will be recorded, and the difference with it.`}
                </span>
              ) : null}
            </label>

            <label className="block">
              <span className="label">Date the shares were allotted</span>
              <input
                type="date"
                value={txnDate}
                onChange={(e) => setTxnDate(e.target.value)}
                className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
              <span className="mt-1 block text-xs text-slate-400">
                Not the date the form was filed — a late filing would move the transaction in or
                out of the twelve-month window Stage 4 tests.
              </span>
            </label>

            <label className="block">
              <span className="label">Share class</span>
              <input
                value={shareClass}
                onChange={(e) => setShareClass(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>

            <label className="block">
              <span className="label">Shares allotted</span>
              <input
                inputMode="decimal"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                className="tnum mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>

            <label className="block">
              <span className="label">What was given for the shares</span>
              <select
                value={considerationKind}
                onChange={(e) => setConsiderationKind(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                <option value="cash">Cash</option>
                <option value="non_cash">Something other than cash</option>
                <option value="mixed">Cash and something else</option>
                <option value="unknown">The form does not say</option>
              </select>
            </label>

            <label className="block">
              <span className="label">Relationship to the company</span>
              <select
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                <option value="unknown">Not established</option>
                <option value="arms_length">Arm’s length</option>
                <option value="connected">Connected party</option>
                <option value="employee">Employee or director</option>
              </select>
              <span className="mt-1 block text-xs text-slate-400">
                {relationship === 'arms_length'
                  ? 'Every methodology will then have to anchor to this transaction or say why not.'
                  : 'The form does not state this. Only an arm’s-length transaction forces anchor-or-justify.'}
              </span>
            </label>

            <label className="block sm:col-span-2">
              <span className="label">Provenance of the figure</span>
              <select
                value={provenance}
                onChange={(e) => setProvenance(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                <option value="registry_confirmed">Read from the filed form</option>
                <option value="management_asserted">Told to us by management</option>
                <option value="corroborated">Told to us and corroborated elsewhere</option>
              </select>
              <span className="mt-1 block text-xs text-slate-400">
                The report writes an asserted figure as “we were informed that…”, never as an
                established fact.
              </span>
            </label>

            <label className="block sm:col-span-2">
              <span className="label">What you checked</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="e.g. Read the price from box 4 of the filed SH01. The shares were fully paid in cash; the subscriber is unconnected with the existing shareholders per the client's confirmation of 3 March."
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
              <span className="mt-1 block text-xs text-slate-400">
                Goes on the transaction and into the defence file. It is the answer to “where did
                this price come from?”, written while you still have the form open.
              </span>
            </label>
          </div>

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => setMode('closed')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!confirmValid || busy}
              onClick={() =>
                run(() =>
                  api.confirmAllotmentReading(engagementId, reading.id, {
                    txnDate,
                    shareClass: shareClass.trim() || undefined,
                    pricePerShare: pricePerShare.trim(),
                    shares: shares.trim() || null,
                    currency: reading.currency ?? undefined,
                    considerationKind,
                    counterpartyRelationship: relationship,
                    provenance,
                    note: note.trim(),
                  }),
                )
              }
            >
              {busy ? 'Recording…' : 'Record as a transaction'}
            </button>
          </div>
        </div>
      ) : null}

      {mode === 'dismiss' ? (
        <div className="mt-4 space-y-3 rounded-lg bg-slate-50/80 p-4">
          <label className="block">
            <span className="label">Why this allotment is not evidence of value</span>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. The shares were allotted to the director in satisfaction of his loan account at par. The price was set by the arrangement rather than negotiated, so it is not evidence of open-market value."
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
            <span className="mt-1 block text-xs text-slate-400">
              The reading is kept, not deleted. This is the answer to “why did you not anchor to
              the transaction three months before the valuation date?”
            </span>
          </label>

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => setMode('closed')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={reason.trim().length < 10 || busy}
              onClick={() =>
                run(() => api.dismissAllotmentReading(engagementId, reading.id, reason.trim()))
              }
            >
              {busy ? 'Recording…' : 'Record the reason'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
